import { formatDateTime } from '@/lib/date-time';
/**
 * @file components/notifications/OfflineTaskPackList.tsx
 * @description 已下載離線任務包列表（純本地 IndexedDB，零後端依賴）。
 *
 * 設計要點：
 * - 資料只來自本地 IndexedDB（`listTaskPacks()`），掛載時讀取一次，
 *   不使用 `useOfflineState` / `apiClient`，因此**不發任何後端請求**，
 *   可安全用於離線、未登入的極簡離線工作區頁。
 * - 「進入」跳轉到對應任務的現場稽核（`/audit-tasks/:id?phase=field`）。
 * - 「刪除本地離線包」屬管理操作（需在線後台），由 `allowDelete` 控制是否顯示；
 *   刪除本身也只動本地快取（`deleteTaskPack`），不請求後端。
 *
 * 被以下兩處復用：
 * - `(public)/me/offline.tsx`：離線現場入口（極簡、無殼、無登入），`allowDelete={false}`。
 * - `components/notifications/OfflineManagementPage.tsx`：在線存儲管理頁，`allowDelete`。
 */

import { useEffect, useState } from 'react';
import { Button, Card, Space, Table, Tag, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { deleteTaskPack, listTaskPacks } from '@/pwa/task-pack-manifest';
import type { CachedTaskPack } from '@/pwa/db';

const { Title } = Typography;

interface OfflineTaskPackListProps {
  /** 是否顯示「刪除本地離線包」操作（僅在線管理頁開啟） */
  allowDelete?: boolean;
  /** 是否顯示卡片內的「離線任務」標題（極簡頁通常由外層自帶標題，可隱藏） */
  showTitle?: boolean;
}

/**
 * 離線任務包列表。
 * 純讀取本地 IndexedDB，不依賴網絡與登入狀態。
 */
export function OfflineTaskPackList({
  allowDelete = false,
  showTitle = false,
}: OfflineTaskPackListProps) {
  const [taskPacks, setTaskPacks] = useState<CachedTaskPack[]>([]);

  useEffect(() => {
    void refreshTaskPacks();
  }, []);

  /** 重新從本地 IndexedDB 載入任務包清單 */
  async function refreshTaskPacks() {
    setTaskPacks(await listTaskPacks());
  }

  /** 進入指定任務的現場稽核（離線專注壳由目標頁自行判斷） */
  function enterTask(taskId: number) {
    window.location.assign(`/audit-tasks/${taskId}?phase=field`);
  }

  /** 刪除本地離線包（僅清本地快取；有未同步資料時阻止刪除） */
  function removeTaskPack(taskId: number) {
    modal.confirm({
      title: '刪除本地離線包？',
      content: '只會刪除已快取的任務資料與文件；若存在未同步資料，系統會阻止刪除。',
      okText: '刪除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await deleteTaskPack(taskId);
        if (!res.ok) {
          message.warning(
            `此任務仍有未同步資料：${res.pendingOutbox} 筆操作、${res.pendingMedia} 個附件`,
          );
          return;
        }
        message.success('本地離線包已刪除');
        await refreshTaskPacks();
      },
    });
  }

  const columns: ColumnsType<CachedTaskPack> = [
    {
      title: '案件',
      dataIndex: 'caseCode',
      render: (v: string | null, row) => v ?? `Case #${row.caseId}`,
    },
    {
      title: '客戶',
      dataIndex: 'clientName',
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      width: 120,
      render: (v: CachedTaskPack['status']) => {
        const color =
          v === 'ready'
            ? 'green'
            : v === 'partial'
              ? 'orange'
              : v === 'quota_limited'
                ? 'red'
                : 'red';
        const label =
          v === 'ready'
            ? '可離線'
            : v === 'partial'
              ? '部分完成'
              : v === 'quota_limited'
                ? '空間不足'
                : '錯誤';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '內容',
      width: 180,
      render: (_, row) => `${row.formsCount} 表單 / ${row.mediaCount} 文件 / ${row.apiCount} 快照`,
    },
    {
      title: '下載時間',
      dataIndex: 'updatedAt',
      width: 180,
      render: (v: number) => formatDateTime(v),
    },
    {
      title: '操作',
      width: allowDelete ? 180 : 96,
      render: (_, row) => (
        <Space>
          <Button
            size="small"
            onPointerDownCapture={(event) => {
              if (event.button !== 0) return;
              event.stopPropagation();
              enterTask(row.taskId);
            }}
            onClickCapture={(event) => {
              event.preventDefault();
              event.stopPropagation();
              enterTask(row.taskId);
            }}
          >
            進入
          </Button>
          {allowDelete && (
            <Button size="small" danger onClick={() => removeTaskPack(row.taskId)}>
              刪除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {showTitle && <Title level={3}>離線任務</Title>}
      <Card>
        <Table<CachedTaskPack>
          rowKey="taskId"
          dataSource={taskPacks}
          columns={columns}
          pagination={false}
          size="small"
          locale={{ emptyText: '尚未下載任務離線包' }}
        />
        <Button size="small" className="mt-3" onClick={() => void refreshTaskPacks()}>
          重新整理
        </Button>
      </Card>
    </div>
  );
}
